import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on, Action } from '@ngrx/store';

import { Message, MessageTemplate, PaginatedRoomMemberList, RoomMember } from '@offconon/core-api';

import { ChatActions } from './chat.actions';
import { ChatEntity, RoomWithMembers } from './chat.models';

export const CHAT_FEATURE_KEY = 'chat';

export interface ChatState extends EntityState<ChatEntity> {
  selectedId?: string | number; // which Chat record has been selected
  loaded: boolean; // has the Chat list been loaded
  error?: string | null; // last known error (if any)
  messageTemplate?: MessageTemplate;
  messages: { [key: number]: Message[] };
  paginatedRoomMemberList?: PaginatedRoomMemberList;

  rooms: {
    [key: string]: {
      rooms: RoomWithMembers[];
      pagination?: {
        page: number;
        pageSize: number;
        total: number;
        totalPages: number;
      };
    };
  };
  roomLoading?: {
    [key: string]: boolean;
  };
  messageLoading?: {
    [key: number]: boolean;
  };
  selectedRoomId?: number;
  roomMembers: { [roomId: number]: RoomMember[] };
}

export interface ChatPartialState {
  readonly [CHAT_FEATURE_KEY]: ChatState;
}

export const chatAdapter: EntityAdapter<ChatEntity> = createEntityAdapter<ChatEntity>();

export const initialChatState: ChatState = chatAdapter.getInitialState({
  // set initial required properties
  loaded: false,
  messages: [],
  rooms: {},
  roomMembers: {},
});

const reducer = createReducer(
  initialChatState,
  on(ChatActions.loadChatSuccess, (state, { chat }) =>
    chatAdapter.setAll(chat, { ...state, loaded: true }),
  ),
  on(ChatActions.loadChatFailure, (state, { error }) => ({ ...state, error })),
  on(ChatActions.fetchMessages, (state, { roomId }) => {
    return {
      ...state,
      messageLoading: {
        [roomId]: true,
      },
    };
  }),
  on(ChatActions.fetchMessagesSuccess, (state, { messages }) => {
    if (!messages[0]?.room_id) {
      return {
        ...state,
        messageLoading: {},
      };
    }
    return {
      ...state,
      messages: {
        ...state.messages,
        [messages[0].room_id!]: messages,
      },
      messageLoading: {
        [messages[0].room_id!]: false,
      },
    };
  }),
  on(ChatActions.addMessage, (state, { message }) => {
    if (!message) {
      return state;
    }

    const existingMessage = state.messages[message.room_id!]?.find((m) => m.id === message.id);
    if (!existingMessage) {
      // Update the room's last_message
      const roomType = state.rooms['direct']?.rooms.find((r) => r.id === message.room_id)
        ? 'direct'
        : 'group';
      const updatedRooms = {
        ...state.rooms,
        [roomType]: {
          ...state.rooms[roomType],
          rooms:
            state.rooms[roomType]?.rooms.map((room) =>
              room.id === message.room_id ? { ...room, last_message: message } : room,
            ) || [],
        },
      };
      return {
        ...state,
        messages: {
          ...state.messages,
          [message.room_id!]: [...(state.messages[message.room_id!] || []), message],
        },
        rooms: updatedRooms,
      };
    }
    return state;
  }),
  on(ChatActions.fetchRooms, (state, { roomType }) => {
    return {
      ...state,
      roomLoading: {
        [roomType || 'direct']: true,
      },
    };
  }),
  on(ChatActions.fetchRoomsSuccess, (state: ChatState, { rooms, pagination, roomType }) => {
    const existingRooms = state.rooms[roomType]?.rooms || [];

    const roomMap = new Map(existingRooms.map((room) => [room.id, room]));

    rooms.forEach((room) => {
      roomMap.set(room.id, room);
    });

    const mergedRooms = Array.from(roomMap.values());

    return {
      ...state,
      rooms: {
        ...state.rooms,
        [roomType]: {
          rooms: mergedRooms,
          pagination,
        },
      },
      roomLoading: {
        [roomType]: false,
      },
      roomPagination: pagination,
    };
  }),

  on(ChatActions.fetchRoomMembersSuccess, (state, { paginatedRoomMemberList, roomId }) => ({
    ...state,
    roomMembers: {
      ...state.roomMembers,
      [roomId]: paginatedRoomMemberList.results || [],
    },
  })),

  on(ChatActions.createRoomMemberSuccess, (state, { roomMember }) => ({
    ...state,
    roomMember,
  })),

  on(ChatActions.createRoomSuccess, (state, { room }) => ({
    ...state,
    rooms: {
      ...state.rooms,
      [room.room_type]: {
        rooms: [...state.rooms[room.room_type]?.rooms, room],
        pagination: state.rooms[room.room_type]?.pagination,
      },
    },
  })),

  on(ChatActions.selectRoom, (state, { id }) => {
    return { ...state, selectedRoomId: id };
  }),
  on(ChatActions.selectRoomSuccess, (state, { id }) => {
    return {
      ...state,
      selectedRoomId: id,
    };
  }),
  on(ChatActions.deleteRoomSuccess, (state, { id }) => {
    const updatedRooms = Object.entries(state.rooms).reduce((acc, [key, value]) => {
      return {
        ...acc,
        [key]: {
          rooms: value.rooms.filter((room) => room.id !== id),
          pagination: {
            ...value.pagination,
            total: (value.pagination?.total || 1) - 1,
          },
        },
      };
    }, {});

    return {
      ...state,
      rooms: updatedRooms,
    };
  }),
  on(ChatActions.leaveRoomSuccess, (state, { roomId }) => {
    const updatedRooms = Object.entries(state.rooms).reduce((acc, [key, value]) => {
      return {
        ...acc,
        [key]: {
          rooms: value.rooms.filter((room) => room.id !== roomId),
          pagination: {
            ...value.pagination,
            total: (value.pagination?.total || 1) - 1,
          },
        },
      };
    }, {});

    return {
      ...state,
      rooms: updatedRooms,
    };
  }),
  on(ChatActions.updateRoomMemberSuccess, (state, { roomMember }) => {
    return {
      ...state,
      roomMembers: {
        ...state.roomMembers,
        [roomMember.room_id]: state.roomMembers[roomMember.room_id].map((member) => {
          if (member.id === roomMember.id) {
            return roomMember;
          }
          return member;
        }),
      },
    };
  }),
);

export function chatReducer(state: ChatState | undefined, action: Action) {
  return reducer(state, action);
}
